import Vue from 'vue';
import { Component, Prop } from 'vue-property-decorator';
import { Menu, MenuItem, MenuItemLookupResult, OrderApiException, OrderErrorCode, OrderMealRequest, OrderMeal, OrderType, OrderWithContext, RecentOrFavoriteEntree, BYOMealType } from '@/models';
import { arrayToMap, formatEntreeContents, getAllMenuItems, isDefined, isMealBYO, isMealBYOProtein, joinWithSuffix } from '@/helpers';
import { Routes } from '@/router';
import { RecentOneTapEntree } from '../cmg-meal-builder-one-tap-reorder';
import { convertRecentAndFavEntreeToOrderMealRequest } from '@/services/order/order-helper';
import { formatRecentEntreeName } from '@/services/recent-and-favorite-orders/recent-and-favorite-orders-helper';
import { AddToBagEvent } from '@/services/analytics/events';
import { AdobeCartAddEvent } from '@/services/analytics/platforms/adobe-analytics/adobe-events/adobe-cart-add';
import { AnalyticsMealType } from '@/services/analytics/platforms/adobe-analytics/models';
import { MaxEntreeErrorEvent } from '@/services/analytics/events/max-byoc-error-event';

const MAX_LENGTH_OF_DETAILS = 75;
@Component({
  name: 'CmgMealBuilderOneTapReorderCard',
})
export default class CmgMealBuilderOneTapReorderCard extends Vue {
  @Prop({ required: true, type: Object })
  recentEntree!: RecentOneTapEntree;

  @Prop({ required: true, type: Object })
  menu!: Menu;
  
  unavailableItems: MenuItem[] = [];

  get menuItemLookupMap(): Map<string, MenuItemLookupResult> | null {
    return this.$appStore.state.menu.menuItemLookupMap;
  }

  get orderDate(): string {
    return this.recentEntree.orderDate
  }

  get formattedOrderEntreeDetails(): string {
    if (!this.recentEntree) {
      return '';
    }

    let items = formatEntreeContents(this.recentEntree.entree, this.menuItemLookupMap)
    const suffix = items.length == 2 ? ' and ' : ', and ';

    if (`${items.join(', ')}${suffix}`.length > MAX_LENGTH_OF_DETAILS) {
      return items.join(', ').substring(0, MAX_LENGTH_OF_DETAILS).trim() + '...';
    }

    return joinWithSuffix(items, ', ', suffix);
  }

  get menuItemName(): string {
    return formatRecentEntreeName(this.recentEntree.entree, this.menuItemLookupMap);
  }

  get hasUnavailableItems(): boolean {
    return this.unavailableItems.length > 0;
  }

  get activeMenu(): Menu | null {
    return this.$appStore.state.menu.activeMenu;
  }

  mounted() {
    if (this.recentEntree.entree.contents) {
      const allItems = arrayToMap(getAllMenuItems(this.menu, true, true), i => i.itemId);
      
      this.unavailableItems = this.recentEntree.entree.contents.map(ec => allItems[ec.menuItemId]).filter(isDefined).filter(i => !i.isItemAvailable);

      const entreeItem = allItems[this.recentEntree.entree.menuItemId];
      if (entreeItem && !entreeItem.isItemAvailable) {
        this.unavailableItems.push(entreeItem);
      }
    }

    this.$services.ui.setShouldBagOpen(true);
  }

  onClickViewDetails(): void {
    this.$services.ui.setModalVisibility('oneTapReorderDetailsModal', true, {
      orderDate: this.orderDate,
      entree: this.recentEntree.entree,
      onAddToBag: () => this.onClickAddItem()
    });
  }

  async onClickAddItem(updatedEntree?: RecentOrFavoriteEntree): Promise<void> { 
      try {
        if (this.recentEntree && !updatedEntree && this.hasUnavailableItems) {
          this.$services.ui.setModalVisibility('oneTapReorderUnavailabilityModal', true, {
            entree: this.recentEntree.entree,
            unavailableItems: this.unavailableItems,
            onAddToBag: (updatedEntree: RecentOrFavoriteEntree) => this.onClickAddItem(updatedEntree)
          });
        } else {
          await this.$services.monoMenu.awaitCacheItemsAndBuildMenu();
          this.addToBag(updatedEntree);
        }
      } catch (e: any) {
        throw e;
      }
  }

  async addToBag(updatedEntree?: RecentOrFavoriteEntree) {
    try {
      const isOrderStart = !this.$services.order.getCurrentOrder();
      const order = this.$services.order.getCurrentOrder() || (await this.$services.order.createOrder(this.$appStore.state.restaurant.selectedRestaurant!.restaurantNumber, OrderType.Regular))!;
      const orderMealRequest = convertRecentAndFavEntreeToOrderMealRequest(updatedEntree ?? this.recentEntree.entree!);
      const preFilledMealName = this.$appStore.state.customer.profile?.firstName || '';
      
      this.$services.ui.setModalVisibility('mealBuilderMealName', true, {
        beforeClose: async mealName => {
          orderMealRequest.meal.mealName = mealName;
          try {
            const mealId = await this.$services.order.addMealToOrder(order, orderMealRequest);
            this.sendAnalyticsEvents(order, orderMealRequest, isOrderStart, mealId);

            if(this.$appStore.state.ui.pceLandingRoute) {
              window.location.assign('/');
            } else {
              this.$services.bag.setDataAnalyticsType('modal');
              this.$services.bag.setBagVisibility(true);
              this.$services.ui.setLastSelectedElement('bag');  
              Vue.nextTick(() => this.$services.ui.focusDiv('.fullModal'));
              this.$router.replace({ name: Routes.Home });
            }
          } catch (e: any) {
            if (e instanceof OrderApiException && e.errors[0].errorCode == OrderErrorCode.MaxEntreeLimitExceeded) {
              this.$services.analytics.send(new MaxEntreeErrorEvent(
                e.errors[0], 
                orderMealRequest.meal.entrees && orderMealRequest.meal.entrees.length > 0 ? orderMealRequest.meal.entrees[0].menuItemName : '',
                this.$router.currentRoute.name ?? ''
              ));
              this.$services.ui.setModalVisibility('maxEntreeLimitModal', true, { errorMessage: e.errors[0].errorData, errorCode: e.errors[0].errorCode });
            } else if (e instanceof OrderApiException && e.errors[0].errorCode == OrderErrorCode.MaxBYOLimitExceeded) {
              this.$services.ui.showMaxBYOFMLimitModal(order, e.errors[0], this.$router.currentRoute.name ?? '', orderMealRequest.meal.entrees ? orderMealRequest.meal.entrees[0]?.menuItemName : '', () => this.replaceMealInOrder(order, updatedEntree!, orderMealRequest));
            } else {
              throw e;
            }
          }
        },
        mealName: preFilledMealName,
      });
    } catch (e: any) {
        throw e;
    }
  }

  private async replaceMealInOrder(order: OrderWithContext, updatedEntree: RecentOrFavoriteEntree, orderMealRequest: OrderMealRequest): Promise<void> {
    const mealToBeReplaced = order?.order.meals?.find(meal => meal.entrees?.find(entree => this.activeMenu?.entrees?.find(e => e.itemType == 'BYOProtein' && entree.menuItemId == e.itemId)));
    try {
      if (order) {
        await this.$services.order.removeMealFromOrder(order, mealToBeReplaced?.mealId ?? '');
        await this.$services.order.addMealToOrder(order, orderMealRequest);
        this.$services.bag.setBagVisibility(true);
        Vue.nextTick(() => this.$services.ui.focusDiv('.fullModal'));
        this.$router.push({ name: Routes.Home });
      }
    } catch (e: any) {
      this.$services.ui.setModalVisibility('globalErrorToast', true);
      this.$services.logger.error('replaceMealInOrder: ', e!);
    } finally {
      this.$services.ui.setModalVisibility('basicError', false);
    }
  }

  // Track how many people use One-Tap Reorder
  private sendAnalyticsEvents(order: OrderWithContext, orderMealRequest: OrderMealRequest, isOrderStart: boolean, mealId: string): void {
    const meal = orderMealRequest.meal as OrderMeal;
    meal.mealId = mealId;
    this.$services.analytics.setAddToBagEventToStore(new AdobeCartAddEvent(new AddToBagEvent(meal, AnalyticsMealType.Standard, isOrderStart, undefined, false, 'one-tap-reorder'), this.$appStore, this.$services));
  }
}
